import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { VesselService } from '@service/vessel.service';
import { AlarmLevel } from 'icreate/common/models/alarm-level';
import { Channel } from 'icreate/common/models/channel';
import { ChannelFcuData } from 'icreate/common/models/channel-fcu-data';
import {
    isAnalog,
    isDigitalContact,
    isDigitalMotor,
    isExhaustGas,
    isFlowCounter,
    isRunningHour,
} from 'icreate/common/models/channel-type';
import { ActiveFlags } from 'icreate/common/models/editor-viewer-element';
import { DefaultColorName, SystemColor, SystemColorType } from 'icreate/common/models/system-color';
import { Vessel } from 'icreate/common/models/vessel';

@Component({
    selector: 'app-alarm-indicators',
    templateUrl: './alarm-indicators.component.html',
    styleUrls: ['./alarm-indicators.component.css'],
    standalone: false,
})
export class AlarmIndicatorsComponent implements OnChanges, OnInit {
    @Input() assignedChannel: Channel;
    @Input() activeFlags: ActiveFlags;
    @Input() vesselId: string;
    @Input() systemColors: SystemColor[];
    @Input() isMotorElement: boolean = false;

    fcuData: ChannelFcuData;
    alarmColorList: SystemColor[] = [];
    sensorfailColorList: SystemColor[] = [];

    constructor(
        private router: Router,
        private vesselService: VesselService
    ) {}

    ngOnInit() {
        this.alarmColorList = this.systemColors.filter(color => color.type == SystemColorType.Alarm);
        this.sensorfailColorList = this.systemColors.filter(color => color.type == SystemColorType.Sensor);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (this.assignedChannel) {
            this.fcuData = this.assignedChannel.fcuData;
        }

        if (!this.activeFlags && this.assignedChannel) {
            this.activeFlags = {
                highHigh: {
                    visible: this.fcuData.alarmSettingVeryHigh.enable ? true : false,
                    level: this.fcuData.alarmSettingVeryHigh.alarmLevel,
                },
                high: {
                    visible: this.fcuData.alarmSettingHigh.enable ? true : false,
                    level: this.fcuData.alarmSettingHigh.alarmLevel,
                },
                low: {
                    visible: this.fcuData.alarmSettingLow.enable ? true : false,
                    level: this.fcuData.alarmSettingLow.alarmLevel,
                },
                lowLow: {
                    visible: this.fcuData.alarmSettingVeryLow.enable ? true : false,
                    level: this.fcuData.alarmSettingVeryLow.alarmLevel,
                },
                outputSensorBreak: {
                    visible: this.fcuData.alarmSettingSensorFail.enable ? true : false,
                    level: this.fcuData.alarmSettingSensorFail.alarmLevel,
                },
                feedbackError: {
                    visible: this.fcuData.alarmSettingFeedbackError.enable ? true : false,
                    level: this.fcuData.alarmSettingFeedbackError.alarmLevel,
                },
                over: { visible: false, level: AlarmLevel.None },
                under: { visible: false, level: AlarmLevel.None },
            };
        }
    }

    getAlarmColor(alarmLevel, colorName = ''): string {
        if (colorName) {
            if (colorName == DefaultColorName.DIGITAL_ON) {
                return this.systemColors.find(color => color.name == DefaultColorName.DIGITAL_ON)?.color;
            } else if (colorName == DefaultColorName.DIGITAL_OFF) {
                return this.systemColors.find(color => color.name == DefaultColorName.DIGITAL_OFF)?.color;
            } else if (colorName == DefaultColorName.MOTOR_RUNNING) {
                return this.systemColors.find(color => color.name == DefaultColorName.MOTOR_RUNNING)?.color;
            } else if (colorName == DefaultColorName.MOTOR_STANDBY) {
                return this.systemColors.find(color => color.name == DefaultColorName.MOTOR_STANDBY)?.color;
            } else if (colorName == DefaultColorName.MOTOR_STOP) {
                return this.systemColors.find(color => color.name == DefaultColorName.MOTOR_STOP)?.color;
            }
            return this.sensorfailColorList?.find(alarmColor => alarmColor.name === colorName)?.color;
        }

        if (alarmLevel === AlarmLevel.Alarm) {
            return this.alarmColorList?.find(alarmColor => alarmColor.name === DefaultColorName.ALARM)?.color;
        } else if (alarmLevel === AlarmLevel.Warning) {
            return this.alarmColorList?.find(alarmColor => alarmColor.name === DefaultColorName.WARNING)?.color;
        } else if (alarmLevel === AlarmLevel.Caution) {
            return this.alarmColorList?.find(alarmColor => alarmColor.name === DefaultColorName.CAUTION)?.color;
        }
    }

    getAlarmLevel(alarmLevel: AlarmLevel): string {
        if (alarmLevel === AlarmLevel.Alarm) {
            return 'mimics.settings.alarm-indicator.level.alarm';
        } else if (alarmLevel === AlarmLevel.Warning) {
            return 'mimics.settings.alarm-indicator.level.warn';
        } else if (alarmLevel === AlarmLevel.Caution) {
            return 'mimics.settings.alarm-indicator.level.caution';
        }
    }

    async openChannelsPage() {
        const vessel: Vessel = this.vesselService.getActiveVessel();

        if (vessel && vessel.shipyardId && vessel.id) {
            const url = this.router.serializeUrl(
                this.router.createUrlTree([`channels/${vessel.shipyardId}/${vessel.id}`])
            );
            window.open(url, '_blank');
        }
    }

    get showAnalogLevels(): boolean {
        if (!this.assignedChannel) return false;
        const channelType = this.assignedChannel.fcuData.channelType;
        return (
            isAnalog(channelType) ||
            isRunningHour(channelType) ||
            isExhaustGas(channelType) ||
            isFlowCounter(channelType)
        );
    }

    get showSensorFailureLevels(): boolean {
        if (!this.assignedChannel) return false;
        const channelType = this.assignedChannel.fcuData.channelType;
        return isAnalog(channelType);
    }

    get showFeedbackLevels(): boolean {
        if (!this.assignedChannel) return false;
        const channelType = this.assignedChannel.fcuData.channelType;
        return isDigitalMotor(channelType);
    }

    get showDigitalLevels(): boolean {
        if (!this.assignedChannel) return false;
        const channelType = this.assignedChannel.fcuData.channelType;
        return isDigitalContact(channelType);
    }

    get alarmIndicators(): ActiveFlags {
        return this.activeFlags;
    }
}
